import { useState } from "react";

import { Mail, Send, Loader2, Calendar } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";

export const ContentRecommendationSettings = () => {
  const [isSending, setIsSending] = useState(false);
  const { execute: invokeEdgeFunction } = useEdgeFunction({ auth: true });

  const sendTestEmail = async () => {
    setIsSending(true);
    try {
      const { error } = await invokeEdgeFunction("send-weekly-content-recommendations", {
        showErrorToast: true,
      });

      if (error) {
        throw error;
      }

      toast.success("Test recommendations sent! Check your email.");
    } catch (error) {
      console.error("Send test error:", error);
      // Error toast already shown by hook if showErrorToast is true
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-primary" />
          Automated Content Recommendations
        </CardTitle>
        <CardDescription>
          Receive weekly emails with content gap analysis and AI-generated suggestions
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <Calendar className="h-4 w-4" />
          <AlertDescription>
            Weekly recommendations are automatically sent every Monday at 9:00 AM UTC to all admin users.
            Each email includes the top 5 priority content gaps with AI-generated suggestions.
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <h4 className="text-sm font-semibold">What's Included:</h4>
          <ul className="text-sm text-muted-foreground space-y-1 ml-4">
            <li>• Priority-ranked content gaps (Critical, High, Medium)</li>
            <li>• Current usage statistics for each category</li>
            <li>• 3 AI-generated content ideas per gap</li>
            <li>• Actionable recommendations for content creation</li>
          </ul>
        </div>

        <div className="pt-4">
          <Button 
            onClick={sendTestEmail} 
            disabled={isSending}
            className="w-full"
          >
            {isSending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending Test Email...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Send Test Email Now
              </>
            )}
          </Button>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            This will send a test email to all admin users with current gap analysis
          </p>
        </div>

      </CardContent>
    </Card>
  );
};
